/**
 * Reactive store
 *
 * Small pub/sub store holding settings, the active kundali and the active palm
 * reading. Settings persist to localStorage; charts persist to IndexedDB.
 *
 * ◆ Deliberately ~150 lines and not a framework. The site's state is a settings
 * object and at most two computed documents; anything larger would be scaffolding
 * for a problem this app does not have.
 *
 * ⚠ **Changing a setting does not mutate an existing chart.** A saved kundali
 * carries the ayanāṁśa, house system and node mode that produced it, and
 * reopening it must show what was saved. Switching the default ayanāṁśa
 * afterwards changes what the *next* chart uses; recomputing an existing one is an
 * explicit action (`recomputeActive`) so the user can see the numbers move.
 *
 * © Manish Jagdish Thatte
 */

import { DEFAULTS, SETTINGS_KEY, THEMES, LOCALES, CHART_STYLES } from './config.js';
import { HOUSE_SYSTEMS } from './astro/houses.js';
import { ayanamsaSystems } from './astro/ayanamsa.js';
import { recomputeWith } from './jyotish/kundali.js';
import { isBrowserTranslateLang } from './utils/browser-translate.js';

const listeners = new Map();   // key → Set<fn>
let nextId = 0;

const state = {
  settings: { ...DEFAULTS },
  kundali: null,
  reading: null,
  storage: null,     // filled by db.available()
  status: null       // transient message for the UI
};

/* ── Validation ───────────────────────────────────────────────────────────── */

/**
 * ⚠ A persisted setting is **untrusted input**. It came from this browser's
 * localStorage, which a previous version of the site wrote, or a user edited, or
 * a half-finished migration left behind. An unknown ayanāṁśa id reaching
 * `ayanamsaValue` throws — correctly — but it would throw on page load, before
 * anything is rendered, which turns a stale preference into a blank site.
 */
const VALIDATORS = {
  locale: (v) => LOCALES.includes(v),
  browserLang: (v) => v == null || isBrowserTranslateLang(v),
  theme: (v) => THEMES.includes(v),
  numerals: (v) => ['locale', 'latin', 'devanagari'].includes(v),
  ayanamsa: (v) => ayanamsaSystems().some((s) => s.id === v),
  houseSystem: (v) => HOUSE_SYSTEMS.includes(v),
  nodeMode: (v) => v === 'mean' || v === 'true',
  chartStyle: (v) => CHART_STYLES.includes(v),
  showEvidence: (v) => typeof v === 'boolean',
  allowNetworkGeocode: (v) => typeof v === 'boolean'
};

/** Keep only values that pass their validator; report what was dropped. */
export function sanitiseSettings(raw) {
  const out = { ...DEFAULTS };
  const dropped = [];
  for (const [key, value] of Object.entries(raw ?? {})) {
    const check = VALIDATORS[key];
    if (!check) { dropped.push({ key, value, why: 'unknown setting' }); continue; }
    if (!check(value)) { dropped.push({ key, value, why: 'not a valid value' }); continue; }
    out[key] = value;
  }
  return { settings: out, dropped };
}

/* ── Read ─────────────────────────────────────────────────────────────────── */

export function getSettings() {
  return { ...state.settings };
}

export function get(key) {
  return state[key];
}

/** The settings a chart computation needs, and nothing else. */
export function chartSettings() {
  const { ayanamsa, houseSystem, nodeMode } = state.settings;
  return { ayanamsa, houseSystem, nodeMode };
}

/* ── Write ────────────────────────────────────────────────────────────────── */

export function setSetting(key, value) {
  if (!VALIDATORS[key]) throw new RangeError(`unknown setting "${key}"`);
  if (!VALIDATORS[key](value)) throw new RangeError(`invalid value for "${key}": ${value}`);
  if (state.settings[key] === value) return;
  state.settings = { ...state.settings, [key]: value };
  persistSettings();
  emit('settings', state.settings);
  emit(`settings.${key}`, value);
}

export function setKundali(kundali) {
  state.kundali = kundali;
  emit('kundali', kundali);
}

export function setReading(reading) {
  state.reading = reading;
  emit('reading', reading);
}

export function setStorage(info) {
  state.storage = info;
  emit('storage', info);
}

/** A transient message: "chart saved", "nothing could be saved". */
export function setStatus(message, severity = 'info') {
  state.status = message ? { message, severity, at: Date.now() } : null;
  emit('status', state.status);
}

/**
 * Recompute the active chart under the current settings.
 *
 * ◆ Explicit, never automatic — see the header note. Returns the new chart so the
 * caller can show what changed rather than silently swapping it.
 */
export function recomputeActive() {
  if (!state.kundali) return null;
  const next = recomputeWith(state.kundali, chartSettings());
  setKundali(next);
  return next;
}

/* ── Subscribe ────────────────────────────────────────────────────────────── */

/**
 * Subscribe to a key. Returns an unsubscribe function.
 *
 * ⚠ Components must call the returned function in `disconnectedCallback`, or a
 * page navigation leaves a listener holding a detached DOM node. The router
 * tears pages down, so a leak here is invisible until the tenth navigation.
 */
export function subscribe(key, fn) {
  if (typeof fn !== 'function') throw new TypeError('subscribe: fn must be a function');
  listeners.get(key) ?? listeners.set(key, new Set());
  listeners.get(key).add(fn);
  const id = ++nextId;
  return function unsubscribe() {
    listeners.get(key)?.delete(fn);
    void id;
  };
}

function emit(key, value) {
  for (const fn of listeners.get(key) ?? []) {
    try {
      fn(value);
    } catch (err) {
      // ⚠ One broken subscriber must not stop the others, or a rendering bug in
      // one component silently freezes every other component's updates.
      console.error(`[store] subscriber for "${key}" threw:`, err);
    }
  }
}

/* ── Persistence ──────────────────────────────────────────────────────────── */

/**
 * Settings live in localStorage rather than IndexedDB, and read synchronously at
 * boot so the first paint already has the right theme and locale.
 *
 * ⚠ Wrapped: localStorage throws in a private window, and a thrown setter here
 * would stop the app before it rendered. Failing to persist a preference is a
 * small loss; failing to start is a total one.
 */
function persistSettings() {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(state.settings));
  } catch {
    // Nothing to do, and nothing to report: the app works, the preference just
    // will not survive a reload. `storage` already tells the user that.
  }
}

/** Load and validate persisted settings. Returns what was dropped, for the UI. */
export function restoreSettings() {
  let raw = null;
  try {
    const text = localStorage.getItem(SETTINGS_KEY);
    raw = text ? JSON.parse(text) : null;
  } catch {
    raw = null;
  }
  const { settings, dropped } = sanitiseSettings(raw);
  state.settings = settings;
  emit('settings', state.settings);
  return { settings, dropped };
}

/** For tests: reset everything without touching storage. */
export function _resetForTests() {
  state.settings = { ...DEFAULTS };
  state.kundali = null;
  state.reading = null;
  state.storage = null;
  state.status = null;
  listeners.clear();
}
